import { DragEndEvent, Modifier, useSensor, useSensors } from '@dnd-kit/core';
import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import NoDnDDataAttributePointerSensor from '~utils/dnd-kit/NoDnDDataAttributePointerSensor';

const VIEWPORT_MARGIN_IN_PX = 8;
// Pointer travel that separates a drag from a click on the handle.
const DRAG_ACTIVATION_DISTANCE_IN_PX = 3;

export interface PanelPosition {
  left: number;
  top: number;
}

export interface PanelSize {
  height: number;
  width: number;
}

export type PanelResizeEdge = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

const RESIZE_DIRECTIONS = {
  e: { horizontal: 1, vertical: 0 },
  n: { horizontal: 0, vertical: -1 },
  ne: { horizontal: 1, vertical: -1 },
  nw: { horizontal: -1, vertical: -1 },
  s: { horizontal: 0, vertical: 1 },
  se: { horizontal: 1, vertical: 1 },
  sw: { horizontal: -1, vertical: 1 },
  w: { horizontal: -1, vertical: 0 },
} satisfies Record<PanelResizeEdge, { horizontal: -1 | 0 | 1; vertical: -1 | 0 | 1 }>;

interface UseDraggableResizablePanelParams {
  bottomInsetInPx: number;
  defaultSizeInPx: PanelSize;
  minSizeInPx: PanelSize;
  positionStorageKey: string;
  sizeStorageKey: string;
}

function hasFiniteNumbers<Key extends string>(
  value: unknown,
  keys: Key[],
): value is Record<Key, number> {
  const candidate = value as Record<string, unknown> | null;

  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    keys.every((key) => Number.isFinite(candidate[key]))
  );
}

function isPanelPosition(value: unknown): value is PanelPosition {
  return hasFiniteNumbers(value, ['left', 'top']);
}

function isPanelSize(value: unknown): value is PanelSize {
  return hasFiniteNumbers(value, ['height', 'width']);
}

function readStoredValue<Value>(
  storageKey: string,
  isValue: (value: unknown) => value is Value,
) {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey) ?? 'null');

    return isValue(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredValue<Value>(storageKey: string, value: Value) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Storage is unavailable in private browsing; the value still holds for this session.
  }
}

function clampBetween(value: number, minimum: number, maximum: number) {
  return Math.max(minimum, Math.min(value, maximum));
}

export default function useDraggableResizablePanel(
  params: UseDraggableResizablePanelParams,
) {
  const {
    bottomInsetInPx,
    defaultSizeInPx,
    minSizeInPx,
    positionStorageKey,
    sizeStorageKey,
  } = params;

  const [position, setPosition] = useState(() =>
    readStoredValue(positionStorageKey, isPanelPosition),
  );
  const [userSize, setUserSize] = useState(() =>
    readStoredValue(sizeStorageKey, isPanelSize),
  );
  const [viewport, setViewport] = useState(() => ({
    height: window.innerHeight,
    width: window.innerWidth,
  }));

  const size = useMemo(
    () =>
      userSize
        ? {
            height: clampBetween(
              userSize.height,
              minSizeInPx.height,
              viewport.height - bottomInsetInPx - 2 * VIEWPORT_MARGIN_IN_PX,
            ),
            width: clampBetween(
              userSize.width,
              minSizeInPx.width,
              viewport.width - 2 * VIEWPORT_MARGIN_IN_PX,
            ),
          }
        : defaultSizeInPx,
    [bottomInsetInPx, defaultSizeInPx, minSizeInPx, userSize, viewport],
  );

  const clampToViewport = useCallback(
    (candidate: PanelPosition) => {
      // Both go negative when the panel is larger than the space it has. Overhanging the
      // top/left edge is deliberate: it keeps the composer and the header controls on screen,
      // matching what the default bottom-right anchor does on a short viewport.
      const furthestLeft = window.innerWidth - size.width - VIEWPORT_MARGIN_IN_PX;
      const furthestTop =
        window.innerHeight - bottomInsetInPx - size.height - VIEWPORT_MARGIN_IN_PX;

      return {
        left: Math.min(Math.max(candidate.left, VIEWPORT_MARGIN_IN_PX), furthestLeft),
        top: Math.min(Math.max(candidate.top, VIEWPORT_MARGIN_IN_PX), furthestTop),
      };
    },
    [bottomInsetInPx, size.height, size.width],
  );

  // A position stored on a wide monitor can land off-screen on a smaller one.
  useLayoutEffect(() => {
    const pullPanelBackOnScreen = () => {
      setViewport((current) =>
        current.height === window.innerHeight && current.width === window.innerWidth
          ? current
          : { height: window.innerHeight, width: window.innerWidth },
      );
      setPosition((current) => {
        if (!current) {
          return null;
        }

        const clamped = clampToViewport(current);

        return clamped.left === current.left && clamped.top === current.top
          ? current
          : clamped;
      });
    };

    pullPanelBackOnScreen();
    window.addEventListener('resize', pullPanelBackOnScreen);

    return () => window.removeEventListener('resize', pullPanelBackOnScreen);
  }, [clampToViewport]);

  const restrictToViewport = useCallback<Modifier>(
    ({ draggingNodeRect, transform }) => {
      if (!draggingNodeRect) {
        return transform;
      }

      const clamped = clampToViewport({
        left: draggingNodeRect.left + transform.x,
        top: draggingNodeRect.top + transform.y,
      });

      return {
        ...transform,
        x: clamped.left - draggingNodeRect.left,
        y: clamped.top - draggingNodeRect.top,
      };
    },
    [clampToViewport],
  );

  const handleDragEnd = useCallback(
    ({ active, delta }: DragEndEvent) => {
      const rectBeforeDrag = active.rect.current.initial;

      if (!rectBeforeDrag) {
        return;
      }

      const dropped = clampToViewport({
        left: rectBeforeDrag.left + delta.x,
        top: rectBeforeDrag.top + delta.y,
      });

      setPosition(dropped);
      writeStoredValue(positionStorageKey, dropped);
    },
    [clampToViewport, positionStorageKey],
  );

  const startResize = useCallback(
    (edge: PanelResizeEdge) => (event: ReactPointerEvent<HTMLElement>) => {
      const handle = event.currentTarget;
      const panel = handle.parentElement;

      if (!panel) {
        return;
      }

      event.preventDefault();
      handle.setPointerCapture(event.pointerId);

      const { horizontal, vertical } = RESIZE_DIRECTIONS[edge];
      const startRect = panel.getBoundingClientRect();
      const startX = event.clientX;
      const startY = event.clientY;
      const bottomLimit = window.innerHeight - bottomInsetInPx - VIEWPORT_MARGIN_IN_PX;

      let latestSize: PanelSize = { height: startRect.height, width: startRect.width };
      let latestPosition: PanelPosition = { left: startRect.left, top: startRect.top };
      let hasMoved = false;
      let isFlushScheduled = false;
      let scheduledFrameId = 0;

      const flushLatestLayout = () => {
        isFlushScheduled = false;
        setUserSize(latestSize);
        setPosition(latestPosition);
      };

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const width = clampBetween(
          startRect.width + horizontal * (moveEvent.clientX - startX),
          minSizeInPx.width,
          horizontal === -1
            ? startRect.right - VIEWPORT_MARGIN_IN_PX
            : window.innerWidth - VIEWPORT_MARGIN_IN_PX - startRect.left,
        );
        const height = clampBetween(
          startRect.height + vertical * (moveEvent.clientY - startY),
          minSizeInPx.height,
          vertical === -1
            ? startRect.bottom - VIEWPORT_MARGIN_IN_PX
            : bottomLimit - startRect.top,
        );

        latestSize = {
          height: vertical === 0 ? startRect.height : height,
          width: horizontal === 0 ? startRect.width : width,
        };
        latestPosition = {
          left: horizontal === -1 ? startRect.right - latestSize.width : startRect.left,
          top: vertical === -1 ? startRect.bottom - latestSize.height : startRect.top,
        };

        hasMoved = true;

        if (!isFlushScheduled) {
          isFlushScheduled = true;
          scheduledFrameId = requestAnimationFrame(flushLatestLayout);
        }
      };

      const handlePointerEnd = () => {
        handle.removeEventListener('pointermove', handlePointerMove);
        handle.removeEventListener('pointerup', handlePointerEnd);
        handle.removeEventListener('pointercancel', handlePointerEnd);

        if (isFlushScheduled) {
          cancelAnimationFrame(scheduledFrameId);
        }

        if (!hasMoved) {
          return;
        }

        flushLatestLayout();
        writeStoredValue(sizeStorageKey, latestSize);
        writeStoredValue(positionStorageKey, latestPosition);
      };

      handle.addEventListener('pointermove', handlePointerMove);
      handle.addEventListener('pointerup', handlePointerEnd);
      handle.addEventListener('pointercancel', handlePointerEnd);
    },
    [bottomInsetInPx, minSizeInPx, positionStorageKey, sizeStorageKey],
  );

  const modifiers = useMemo(() => [restrictToViewport], [restrictToViewport]);
  const sensors = useSensors(
    useSensor(NoDnDDataAttributePointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_IN_PX },
    }),
  );

  return { modifiers, onDragEnd: handleDragEnd, position, sensors, size, startResize };
}
